import React, { useEffect, useState } from 'react';
import { Tooltip } from 'reablocks';
import { VendorLogo } from 'shared/utils/VendorLogo';
import css from './ThreatLandscape.module.css';
import threatGroupsData from './threatgroups.json';
import campaignsData from './campaigns.json';

// Simple mappings for now to avoid import issues
const INDUSTRIES = {
  FINANCE_AND_INSURANCE: 'Finance and Insurance',
  HEALTH_CARE_AND_SOCIAL_ASSISTANCE: 'Health Care and Social Assistance',
  PUBLIC_ADMINISTRATION: 'Public Administration',
  UTILITIES: 'Utilities',
  RETAIL_TRADE: 'Retail Trade',
  INFORMATION: 'Information'
};

const COUNTRIES = {
  USA: 'United States',
  GBR: 'United Kingdom',
  CAN: 'Canada',
  DEU: 'Germany',
  FRA: 'France',
  JPN: 'Japan',
  AUS: 'Australia'
};

// Platform-specific CVE mappings
const PLATFORM_CVES = {
  Windows: [
    'CVE-2025-53770',
    'CVE-2025-49704',
    'CVE-2025-49706',
    'CVE-2025-29824',
    'CVE-2025-24985',
    'CVE-2025-24993'
  ],
  Linux: [
    'CVE-2025-0282',
    'CVE-2025-22457',
    'CVE-2025-4427',
    'CVE-2025-4428',
    'CVE-2025-31324'
  ],
  macOS: ['CVE-2025-6558', 'CVE-2025-24085', 'CVE-2024-44243'],
  IaaS: [],
  SaaS: [],
  Containers: [],
  Network: ['CVE-2025-5777', 'CVE-2025-25257']
};

// CVE descriptions for hover tooltips
const CVE_DESCRIPTIONS = {
  'CVE-2025-53770':
    "Microsoft SharePoint deserialization vulnerability allowing unauthenticated remote code execution, part of 'ToolShell' exploit chain actively exploited in mass attacks",
  'CVE-2025-49704':
    'Microsoft SharePoint code injection vulnerability enabling remote code execution when chained with authentication bypass, originally demonstrated at Pwn2Own Berlin',
  'CVE-2025-49706':
    'Microsoft SharePoint spoofing vulnerability allowing authentication bypass through HTTP Referer header manipulation, used in exploit chains',
  'CVE-2025-29824':
    'Windows Common Log File System (CLFS) use-after-free vulnerability enabling privilege escalation to SYSTEM, linked to ransomware attacks',
  'CVE-2025-24985':
    'Windows Fast FAT File System Driver buffer overflow enabling remote code execution through malicious VHD files, actively exploited as zero-day',
  'CVE-2025-24993':
    'Windows NTFS heap-based buffer overflow vulnerability allowing remote code execution, exploited in the wild before patching',
  'CVE-2025-0282':
    'Ivanti Connect Secure unauthenticated stack-based buffer overflow enabling remote code execution, exploited by China-nexus UNC5221 group',
  'CVE-2025-22457':
    'Ivanti Connect Secure buffer overflow vulnerability initially thought unexploitable, later weaponized for remote code execution by studying patches',
  'CVE-2025-4427':
    'Ivanti EPMM authentication bypass vulnerability allowing unauthenticated access to restricted functionality when chained with RCE flaw',
  'CVE-2025-4428':
    'Ivanti EPMM post-authentication remote code execution through unsafe Expression Language injection in error message handling',
  'CVE-2025-31324':
    'SAP NetWeaver Visual Composer missing authorization check allowing unauthenticated file upload and remote code execution, CVSS 10.0 critical',
  'CVE-2025-6558':
    'Google Chrome ANGLE/GPU input validation flaw enabling sandbox escape through crafted HTML pages, fifth Chrome zero-day of 2025',
  'CVE-2025-24085':
    'Apple CoreMedia use-after-free vulnerability allowing privilege escalation through malicious applications, actively exploited against iOS devices',
  'CVE-2024-44243':
    'macOS System Integrity Protection (SIP) bypass through kernel extensions, allowing rootkit installation and persistent malware deployment',
  'CVE-2025-5777':
    'Citrix NetScaler out-of-bounds read vulnerability (CitrixBleed 2) enabling memory disclosure and session token theft from VPN appliances',
  'CVE-2025-25257':
    'Fortinet FortiWeb SQL injection vulnerability in Fabric Connector allowing unauthenticated remote code execution through crafted HTTP requests'
};

// Campaign display limit
const MAX_CAMPAIGNS_TO_SHOW = 2;

// Threat groups and vulnerabilities display limits
const MAX_THREAT_GROUPS_TO_SHOW = 7;
const MAX_VULNERABILITIES_TO_SHOW = 5;

// Tooltip control
const TOOLTIPS_ENABLED = false;

// Helper to get matching campaigns based on selected industries/countries
function getRecentCampaigns(industries: string[], countries: string[]) {
  const campaigns = campaignsData.data.prioritizedCampaigns;
  const matches: Array<{
    name: string;
    description: string;
    severity: 'high' | 'medium' | 'low';
  }> = [];

  // Priority 1: Match both country and industry
  for (const campaign of campaigns) {
    if (matches.length >= MAX_CAMPAIGNS_TO_SHOW) break;
    const industryMatch = campaign.industries?.some((ind: string) =>
      industries.includes(ind)
    );
    const countryMatch = campaign.countries?.some((c: string) =>
      countries.includes(c)
    );
    if (industryMatch && countryMatch) {
      matches.push({
        name: campaign.name,
        description: campaign.description,
        severity: 'high' // Default severity since campaigns.json doesn't have severity
      });
    }
  }

  // Priority 2: Match only industry (if we need more campaigns)
  if (matches.length < MAX_CAMPAIGNS_TO_SHOW) {
    for (const campaign of campaigns) {
      if (matches.length >= MAX_CAMPAIGNS_TO_SHOW) break;
      const industryMatch = campaign.industries?.some((ind: string) =>
        industries.includes(ind)
      );
      const countryMatch = campaign.countries?.some((c: string) =>
        countries.includes(c)
      );
      // Skip if already added or if it matches country but not industry
      if (
        !matches.some(m => m.name === campaign.name) &&
        industryMatch &&
        !countryMatch
      ) {
        matches.push({
          name: campaign.name,
          description: campaign.description,
          severity: 'high'
        });
      }
    }
  }

  // Priority 3: Match only country (if we need more campaigns)
  if (matches.length < MAX_CAMPAIGNS_TO_SHOW) {
    for (const campaign of campaigns) {
      if (matches.length >= MAX_CAMPAIGNS_TO_SHOW) break;
      const industryMatch = campaign.industries?.some((ind: string) =>
        industries.includes(ind)
      );
      const countryMatch = campaign.countries?.some((c: string) =>
        countries.includes(c)
      );
      // Skip if already added or if it matches industry but not country
      if (
        !matches.some(m => m.name === campaign.name) &&
        countryMatch &&
        !industryMatch
      ) {
        matches.push({
          name: campaign.name,
          description: campaign.description,
          severity: 'medium'
        });
      }
    }
  }

  // Priority 4: Fill remaining slots with first available campaigns
  if (matches.length < MAX_CAMPAIGNS_TO_SHOW) {
    for (const campaign of campaigns) {
      if (matches.length >= MAX_CAMPAIGNS_TO_SHOW) break;
      if (!matches.some(m => m.name === campaign.name)) {
        matches.push({
          name: campaign.name,
          description: campaign.description,
          severity: 'low'
        });
      }
    }
  }

  return matches;
}

interface ThreatLandscapeProps {
  industries: string[];
  countries: string[];
  platforms: string[];
  products: string[];
}

interface Campaign {
  name: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
}

interface ThreatData {
  threats: string[];
  campaigns: Campaign[];
  vulnerabilities: string[];
}

// Helper to get matching threat groups based on selected industries/countries
function getActiveThreatGroups(industries: string[], countries: string[]) {
  const matches: { name: string; description: string }[] = [];
  for (const [name, group] of Object.entries(
    threatGroupsData as Record<string, any>
  )) {
    const industryMatch = group.industries?.some((ind: string) =>
      industries.includes(ind)
    );
    const countryMatch = group.countries?.some((c: string) =>
      countries.includes(c)
    );
    if (industryMatch || countryMatch) {
      matches.push({ name, description: group.description });
    }
  }
  // If no matches and there is a selection, show fallback
  if (matches.length === 0 && (industries.length > 0 || countries.length > 0)) {
    return [
      {
        name: 'Scattered Spider',
        description: threatGroupsData['Scattered Spider'].description
      },
      { name: 'Cactus', description: threatGroupsData['Cactus'].description },
      {
        name: 'RansomHub',
        description: threatGroupsData['RansomHub'].description
      }
    ];
  }
  return matches;
}

const ThreatLandscape: React.FC<ThreatLandscapeProps> = ({
  industries,
  countries,
  platforms,
  products
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (industries.length > 0) {
      setIsLoading(true);
      setAnimationKey(prev => prev + 1);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [industries, countries, platforms, products]);

  const getSeverityIndicator = (severity: string) => {
    const className =
      severity === 'high'
        ? css.severityHigh
        : severity === 'medium'
          ? css.severityMedium
          : css.severityLow;
    return <span className={`${css.severityIndicator} ${className}`}></span>;
  };

  const getSelectedCountryNames = () => {
    return countries
      .map(countryCode => {
        const countryName = COUNTRIES[countryCode];
        return countryName
          ? decodeURIComponent(JSON.parse(`"${countryName}"`))
          : countryCode;
      })
      .join(', ');
  };

  const getSelectedIndustryNames = () => {
    return industries
      .map(industryCode => {
        // First check if we have a mapping in INDUSTRIES
        if (INDUSTRIES[industryCode]) {
          return INDUSTRIES[industryCode];
        }
        // Otherwise convert from code format to title case
        return industryCode
          .split('_')
          .map(
            word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
      })
      .join(', ');
  };

  const getPlatformCVEs = () => {
    const cves = new Set<string>();
    platforms.forEach(platform => {
      const platformCVEs = PLATFORM_CVES[platform];
      if (platformCVEs) {
        platformCVEs.forEach(cve => cves.add(cve));
      }
    });
    return Array.from(cves);
  };

  const activeThreatGroups = getActiveThreatGroups(industries, countries);
  const recentCampaigns = getRecentCampaigns(industries, countries);
  const platformCVEs = getPlatformCVEs();
  const hasThreatGroups = activeThreatGroups.length > 0;
  const hasCampaigns = recentCampaigns.length > 0;
  const hasVulnerabilities = platformCVEs.length > 0;

  if (!industries.length) {
    return (
      <div className={css.threatLandscape}>
        <div className={css.previewHeader}>
          <h3>Your Threat Landscape</h3>
          <p>Select your industry to see relevant threats</p>
        </div>
        <div className={css.placeholderContent}>
          <div className={css.placeholderCard}>
            <div className={css.placeholderText}>
              Choose your industry, country, and platforms to see personalized
              threat intelligence
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.threatLandscape} key={animationKey}>
      <div className={css.previewHeader}>
        <h3>
          Your Threat Landscape
          {isLoading && <span className={css.loading}></span>}
        </h3>
        <p>
          Sample report for {getSelectedIndustryNames()}
          {countries.length > 0 && ` in ${getSelectedCountryNames()}`}. Complete
          this form to get your full threat landscape assessment report.
        </p>
      </div>
      <div className={css.threatInsights}>
        {isLoading ? (
          <div className={css.loadingState}>
            <div className={css.loadingCard}></div>
            <div className={css.loadingCard}></div>
            <div className={css.loadingCard}></div>
          </div>
        ) : (
          <>
            {/* Recent Campaigns */}
            {hasCampaigns && (
              <>
                {recentCampaigns.map((campaign, index) => (
                  <div
                    key={campaign.name}
                    className={css.insightCard}
                    style={{ animationDelay: `${index * 0.15}s` }}
                  >
                    <h4>
                      {getSeverityIndicator(campaign.severity)}
                      Recent Campaign
                    </h4>
                    <div className={css.campaignName}>{campaign.name}</div>
                    <p>{campaign.description}</p>
                  </div>
                ))}
              </>
            )}

            {/* Threat Groups */}
            {hasThreatGroups && (
              <div
                className={css.insightCard}
                style={{ animationDelay: `${recentCampaigns.length * 0.15}s` }}
              >
                <h4>Active Threat Groups</h4>
                <div className={css.threatTags}>
                  {activeThreatGroups
                    .slice(0, MAX_THREAT_GROUPS_TO_SHOW)
                    .map(threat =>
                      TOOLTIPS_ENABLED ? (
                        <Tooltip
                          key={threat.name}
                          content={
                            threat.description || 'No description available'
                          }
                          placement="top"
                        >
                          <span className={css.threatTag}>{threat.name}</span>
                        </Tooltip>
                      ) : (
                        <span key={threat.name} className={css.threatTag}>
                          {threat.name}
                        </span>
                      )
                    )}
                  {activeThreatGroups.length > MAX_THREAT_GROUPS_TO_SHOW && (
                    <span className={css.threatTag}>...</span>
                  )}
                </div>
              </div>
            )}
            {/* Vulnerabilities */}
            {platforms.length > 0 && hasVulnerabilities && (
              <div
                className={css.insightCard}
                style={{
                  animationDelay: `${(recentCampaigns.length + 1) * 0.15}s`
                }}
              >
                <h4>Platform Vulnerabilities</h4>
                <p>
                  Critical vulnerabilities affecting your selected platforms:{' '}
                  {platforms.join(', ')}
                </p>
                <div className={css.threatTags}>
                  {platformCVEs
                    .slice(0, MAX_VULNERABILITIES_TO_SHOW)
                    .map(vuln =>
                      TOOLTIPS_ENABLED ? (
                        <Tooltip
                          key={vuln}
                          content={
                            CVE_DESCRIPTIONS[vuln] || 'No description available'
                          }
                          placement="top"
                        >
                          <span className={css.threatTag}>{vuln}</span>
                        </Tooltip>
                      ) : (
                        <span key={vuln} className={css.threatTag}>
                          {vuln}
                        </span>
                      )
                    )}
                  {platformCVEs.length > MAX_VULNERABILITIES_TO_SHOW && (
                    <span className={css.threatTag}>...</span>
                  )}
                </div>
              </div>
            )}
            {/* Security Products Coverage */}
            {products.length > 0 && (
              <div
                className={css.insightCard}
                style={{
                  animationDelay: `${(recentCampaigns.length + 2) * 0.15}s`
                }}
              >
                <h4>Security Coverage</h4>
                <p>Your selected security products:</p>
                <div className={css.securityProducts}>
                  {products.map(product => (
                    <div key={product} className={css.securityProduct}>
                      <div className={css.securityProductIcon}>
                        <VendorLogo vendor={product} alt={product} />
                      </div>
                      <div className={css.securityProductName}>{product}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Only show no data if nothing else is present */}
            {!hasThreatGroups && !hasVulnerabilities && !hasCampaigns && (
              <div className={css.noDataCard}>
                <h4>No threat data available</h4>
                <p>
                  Try selecting a different industry or check back later for
                  updates.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ThreatLandscape;
